import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, FileText, Sparkles, Loader2, User } from 'lucide-react';
import { apiFetch } from '../../api';
import { useWorkflow } from '../../context/WorkflowContext';
import useScopedStudents from '../../hooks/useScopedStudents';
import { getStudentDisplayName } from '../../utils/studentName';
import '../../styles/whiteboard-session.css';

/**
 * SessionsPanel
 *
 * Collapsible panel in the whiteboard sidebar showing:
 * - Student selector (inherits from WorkflowContext, allows override)
 * - List of sessions for selected student
 * - Generate button per session to create whiteboard content
 */
const SessionsPanel = ({
  onGenerateContent,
  isGenerating,
  generatingSessionId
}) => {
  const { studentContext } = useWorkflow();

  // Panel state
  const [isExpanded, setIsExpanded] = useState(true);

  // Student selection state (override of workflow context)
  const [selectedStudentOverride, setSelectedStudentOverride] = useState(null);
  const { students, studentsLoading: loadingStudents } = useScopedStudents();

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Computed active student (override takes precedence)
  const activeStudentUuid = selectedStudentOverride || studentContext?.uuid || null;
  const activeStudentName = selectedStudentOverride
    ? getStudentDisplayName(students.find(s => s.uuid === selectedStudentOverride) || {})
    : studentContext?.name || null;

  // If selected override becomes invalid after scoped list refresh, clear it.
  useEffect(() => {
    if (!selectedStudentOverride) return;
    const exists = students.some(s => s.uuid === selectedStudentOverride);
    if (!exists) {
      setSelectedStudentOverride(null);
    }
  }, [students, selectedStudentOverride]);

  // Load sessions when active student changes
  useEffect(() => {
    if (activeStudentUuid) {
      loadSessions(activeStudentUuid);
    } else {
      setSessions([]);
    }
  }, [activeStudentUuid]);

  const loadSessions = async (studentUuid) => {
    setLoadingSessions(true);
    try {
      const response = await apiFetch(`/api/sessions/list?student_id=${studentUuid}`);
      if (response.ok) {
        const data = await response.json();
        // Take only the 7 most recent sessions
        setSessions((data.sessions || []).slice(0, 7));
      }
    } catch (err) {
      console.error('Error loading sessions:', err);
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleStudentChange = (e) => {
    const uuid = e.target.value;
    setSelectedStudentOverride(uuid || null);
  };

  const handleGenerate = (session) => {
    if (onGenerateContent && !isGenerating) {
      onGenerateContent(session.id, session);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getTopicsPreview = (session) => {
    const topics = session.summary?.topics_covered || session.topics_covered || [];
    if (topics.length === 0) return '';
    return topics.slice(0, 2).join(', ') + (topics.length > 2 ? '...' : '');
  };

  return (
    <div className="sessions-panel">
      {/* Collapsible Header */}
      <button
        className="sessions-panel__header"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className="sessions-panel__title">
          <FileText size={14} />
          Sessions
        </span>
        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {/* Collapsible Content */}
      <div className={`sessions-panel__content ${!isExpanded ? 'sessions-panel__content--collapsed' : ''}`}>
        {/* Student Selector */}
        <div className="sessions-panel__student-selector">
          <div className="sessions-panel__student-label">
            <User size={12} />
            <span>Student</span>
          </div>
          <select
            className="sessions-panel__student-dropdown"
            value={selectedStudentOverride || studentContext?.uuid || ''}
            onChange={handleStudentChange}
            disabled={loadingStudents}
          >
            <option value="">
              {studentContext?.name
                ? `${studentContext.name} (workflow)`
                : 'Select a student...'}
            </option>
            {students.map(student => (
              <option key={student.uuid} value={student.uuid}>
                {getStudentDisplayName(student)}
              </option>
            ))}
          </select>
        </div>

        {/* Sessions List */}
        <div className="sessions-panel__list">
          {loadingSessions ? (
            <div className="sessions-panel__loading">
              <Loader2 size={16} className="sessions-panel__spinner" />
              <span>Loading sessions...</span>
            </div>
          ) : !activeStudentUuid ? (
            <div className="sessions-panel__empty">
              Select a student to view their sessions
            </div>
          ) : sessions.length === 0 ? (
            <div className="sessions-panel__empty">
              No sessions found for this student
            </div>
          ) : (
            sessions.map(session => (
              <div key={session.id} className="sessions-panel__item">
                <div className="sessions-panel__item-header">
                  <span className="sessions-panel__item-title">
                    {session.title || session.id}
                  </span>
                  <span className="sessions-panel__item-date">
                    {formatDate(session.date || session.created_at)}
                  </span>
                </div>
                {getTopicsPreview(session) && (
                  <div className="sessions-panel__item-topics">
                    {getTopicsPreview(session)}
                  </div>
                )}
                <button
                  className={`sessions-panel__generate-btn ${
                    isGenerating && generatingSessionId === session.id
                      ? 'sessions-panel__generate-btn--loading'
                      : ''
                  }`}
                  onClick={() => handleGenerate(session)}
                  disabled={isGenerating}
                  title="Generate practice problems and guides from this session"
                >
                  {isGenerating && generatingSessionId === session.id ? (
                    <>
                      <Loader2 size={12} className="sessions-panel__spinner" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={12} />
                      Generate
                    </>
                  )}
                </button>
              </div>
            ))
          )}
        </div>

        {/* Student Context Indicator */}
        {activeStudentUuid && activeStudentName && (
          <div className="sessions-panel__context-indicator">
            Viewing sessions for: <strong>{activeStudentName}</strong>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionsPanel;
